import React from 'react'
import useReportElementChart from 'hooks/useReportElementChart'
import { Box, Collapse, Typography } from '@mui/material'
import { PercentileChartElementBarWithTooltip } from 'components/PercentileChartElement/Bar'
import getCohortData from 'util/getCohortData'
import PercentileChartElementCohort, {
  PercentileCohortData,
} from 'components/PercentileChartElement/Cohort'
import PercentileChartElementAxis from 'components/PercentileChartElement/Axis'
import ReportElementSettings from './ReportElementSettings'
import ReportElementLegend from './ReportElementLegend'
import useElementCohortsAndSubgroups from 'hooks/useElementCohortsAndSubgroups'
import { getCohortToUse, getSubgroupToUse } from 'util/getBarChartData'
import { ReportElementProps } from './ReportElement'

type datumType = {
  percentage: number
  name: string
  value: number
  significance: string
}

const ReportElementPercentileChart: React.FC<ReportElementProps> = ({
  reportId,
  report,
  element,
  sectionId,
  favorites = false,
  print = false,
  onUpdateFavorites,
}) => {
  const {
    cohort,
    subgroup,
    config,
    pastResults,
    data,
    parsedContent,
    parsedData,
    elementTitle,
    hidden,
    handleSetHidden,
  } = useReportElementChart(element, reportId)

  try {
    const [elementCohorts, elementSubgroups] = useElementCohortsAndSubgroups(
      element,
      parsedData
    )

    const cohortToUse: string = getCohortToUse(cohort || '', elementCohorts)
    const subgroupToUse: string = getSubgroupToUse(
      subgroup || '',
      elementSubgroups
    )

    const subgroupData = subgroupToUse
      ? data.segmentations.filter(
          (d: { name: string }) => d.name === subgroupToUse
        )[0]
      : data.segmentations[0]

    const cohortData = getCohortData({
      cohortName: cohortToUse || undefined,
      cohorts: data.cohorts,
      cohortSynonyms: parsedData.cohortSynonyms,
    }) as PercentileCohortData | undefined

    const pastOptions = pastResults
      ? (data['past_results'] || []).map((d: { name: string }) => ({
          key: d.name,
          title: d.name,
        }))
      : []
    const staticOptions = (data.static_segmentations || []).map(
      (d: { name: string }) => ({ key: d.name, title: d.name })
    )
    const segOptions = (subgroupData?.values || []).map(
      (d: { name: string }) => ({
        key: d.name,
        title: d.name,
      })
    )
    const showHideOptions = [...pastOptions, ...staticOptions, ...segOptions]

    if (print) {
      return (
        <Box display="block" mb={4} style={{ breakInside: 'avoid' }}>
          <table style={{ width: '100%' }}>
            <thead>
              <tr style={{ breakInside: 'avoid' }}>
                <th style={{ textAlign: 'left' }}>
                  {parsedContent.use_default_values && data.superTitle && (
                    <Typography className="superTitle" variant="h5">
                      {data.superTitle}
                    </Typography>
                  )}
                  {!parsedContent.use_default_values &&
                    parsedContent.supertitle && (
                      <Typography className="supertitle" variant="h5">
                        {parsedContent.supertitle}
                      </Typography>
                    )}
                  {elementTitle && (
                    <Typography className="question" variant="h5">
                      {elementTitle}
                    </Typography>
                  )}
                  <ReportElementLegend content={parsedContent} data={data} />
                  <Box mt={2}>
                    <ReportElementSettings
                      print={print}
                      reportId={reportId}
                      element={element}
                      report={report}
                      sectionId={sectionId}
                      noPastResults={data['past_results'].length === 0}
                      showHideOptions={showHideOptions}
                      hidden={hidden}
                      onChangeHidden={handleSetHidden}
                      favorites={favorites}
                      onUpdateFavorites={onUpdateFavorites}
                    />
                  </Box>
                  <PercentileChartElementAxis
                    absolutes={data.absolutes}
                    significantDigits={parsedContent['significant_digits']}
                    prefix={data.prefix}
                    suffix={data.suffix}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ breakInside: 'avoid' }}>
                <td>
                  <PercentileChartElementBarWithTooltip
                    showTooltipLabel
                    tooltipValue="percentile"
                    percentage={data.current.percentage}
                    showBoldValue
                    showNormalPercentile
                    label={data.current.name}
                    value={data.current.value}
                    prefix={data.prefix}
                    suffix={data.suffix}
                    significantDigits={parsedContent['significant_digits']}
                    size={80}
                    color="primary"
                    significance={data.current.significance ? true : false}
                  />
                </td>
              </tr>
              {cohortData && (
                <tr style={{ breakInside: 'avoid' }}>
                  <td>
                    <PercentileChartElementCohort
                      data={
                        cohortData || { max: 0, median: 0, min: 0, name: '' }
                      }
                      size={40}
                    />
                  </td>
                </tr>
              )}
              {pastResults &&
                data['past_results'].map((d: datumType, i: number) => {
                  if (!hidden.includes(d.name)) {
                    return (
                      <tr key={i} style={{ breakInside: 'avoid' }}>
                        <td>
                          <PercentileChartElementBarWithTooltip
                            showTooltipLabel
                            tooltipValue="percentile"
                            showNormalValue
                            percentage={d.percentage}
                            label={d.name}
                            value={d.value}
                            prefix={data.prefix}
                            suffix={data.suffix}
                            significantDigits={
                              parsedContent['significant_digits']
                            }
                            size={40}
                            color="primary"
                            significance={d.significance ? true : false}
                          />
                        </td>
                      </tr>
                    )
                  }
                  return null
                })}
              {(data.static_segmentations || []).map(
                (d: datumType, i: number) => {
                  if (!hidden.includes(d.name)) {
                    return (
                      <tr key={i} style={{ breakInside: 'avoid' }}>
                        <td>
                          <PercentileChartElementBarWithTooltip
                            showTooltipLabel
                            tooltipValue="percentile"
                            showNormalValue
                            percentage={d.percentage}
                            label={d.name}
                            value={d.value}
                            prefix={data.prefix}
                            suffix={data.suffix}
                            significantDigits={
                              parsedContent['significant_digits']
                            }
                            size={40}
                            color="static"
                            significance={d.significance ? true : false}
                          />
                        </td>
                      </tr>
                    )
                  }
                  return null
                }
              )}
              {(subgroupData?.values || []).map((d: datumType, i: number) => {
                if (!hidden.includes(d.name)) {
                  return (
                    <tr key={i} style={{ breakInside: 'avoid' }}>
                      <td>
                        <PercentileChartElementBarWithTooltip
                          showTooltipLabel
                          tooltipValue="percentile"
                          showNormalValue
                          percentage={d.percentage}
                          label={d.name}
                          value={d.value}
                          prefix={data.prefix}
                          suffix={data.suffix}
                          significantDigits={
                            parsedContent['significant_digits']
                          }
                          size={40}
                          color="secondary"
                          significance={d.significance ? true : false}
                        />
                      </td>
                    </tr>
                  )
                }
                return null
              })}
            </tbody>
          </table>
        </Box>
      )
    }

    return (
      <Box sx={{ width: '100%', mb: 4, breakInside: 'avoid' }}>
        <Box px={print ? 0 : 4}>
          {parsedContent.use_default_values && data.superTitle && (
            <Typography className="superTitle" variant="h5">
              {data.superTitle}
            </Typography>
          )}
          {!parsedContent.use_default_values && parsedContent.supertitle && (
            <Typography className="supertitle" variant="h5">
              {parsedContent.supertitle}
            </Typography>
          )}
          {elementTitle && (
            <Typography className="question" variant="h5">
              {elementTitle}
            </Typography>
          )}
        </Box>
        <Box mt={2}>
          <ReportElementSettings
            print={print}
            reportId={reportId}
            element={element}
            report={report}
            sectionId={sectionId}
            noPastResults={data['past_results'].length === 0}
            showHideOptions={showHideOptions}
            hidden={hidden}
            onChangeHidden={handleSetHidden}
            favorites={favorites}
            onUpdateFavorites={onUpdateFavorites}
          />
        </Box>
        <ReportElementLegend content={parsedContent} data={data} />
        <Box py={4} px={print ? 0 : 4} sx={{ breakInside: 'avoid' }}>
          <PercentileChartElementAxis
            absolutes={data.absolutes}
            significantDigits={parsedContent['significant_digits']}
            prefix={data.prefix}
            suffix={data.suffix}
          />
          <PercentileChartElementBarWithTooltip
            showTooltipLabel
            tooltipValue="percentile"
            percentage={data.current.percentage}
            showBoldValue
            showNormalPercentile
            label={data.current.name}
            value={data.current.value}
            prefix={data.prefix}
            suffix={data.suffix}
            significantDigits={parsedContent['significant_digits']}
            size={80}
            color="primary"
            significance={data.current.significance ? true : false}
          />
          <Collapse
            in={cohortData ? true : false}
            timeout={1000}
            sx={{ breakInside: 'avoid' }}
          >
            <PercentileChartElementCohort
              data={cohortData || { max: 0, median: 0, min: 0, name: '' }}
              size={40}
            />
          </Collapse>
          <Collapse
            in={pastResults || false}
            timeout={1000}
            sx={{ breakInside: 'avoid' }}
          >
            {data['past_results'].map((d: datumType, i: number) => (
              <Collapse key={i} in={!hidden.includes(d.name)}>
                <PercentileChartElementBarWithTooltip
                  showTooltipLabel
                  tooltipValue="percentile"
                  showNormalValue
                  percentage={d.percentage}
                  label={d.name}
                  value={d.value}
                  prefix={data.prefix}
                  suffix={data.suffix}
                  significantDigits={parsedContent['significant_digits']}
                  size={40}
                  color="primary"
                  significance={d.significance ? true : false}
                />
              </Collapse>
            ))}
          </Collapse>
          {(data.static_segmentations || []).map((d: datumType, i: number) => (
            <Collapse
              key={i}
              in={!hidden.includes(d.name)}
              timeout={500}
              sx={{ breakInside: 'avoid' }}
            >
              <PercentileChartElementBarWithTooltip
                showTooltipLabel
                tooltipValue="percentile"
                showNormalValue
                percentage={d.percentage}
                label={d.name}
                value={d.value}
                prefix={data.prefix}
                suffix={data.suffix}
                significantDigits={parsedContent['significant_digits']}
                size={40}
                color="static"
                significance={d.significance ? true : false}
              />
            </Collapse>
          ))}
          {(subgroupData?.values || []).map((d: datumType, i: number) => (
            <Collapse
              key={i}
              in={!hidden.includes(d.name)}
              timeout={500}
              sx={{ breakInside: 'avoid' }}
            >
              <PercentileChartElementBarWithTooltip
                showTooltipLabel
                tooltipValue="percentile"
                showNormalValue
                percentage={d.percentage}
                label={d.name}
                value={d.value}
                prefix={data.prefix}
                suffix={data.suffix}
                significantDigits={parsedContent['significant_digits']}
                size={40}
                color="secondary"
                significance={d.significance ? true : false}
              />
            </Collapse>
          ))}
        </Box>
      </Box>
    )
  } catch (error) {
    console.error(error)
    return null
  }
}

export default ReportElementPercentileChart
